import { motion } from 'framer-motion';
import PageBanner from '../../components/ui/PageBanner';
import SectionHeading from '../../components/ui/SectionHeading';
import Button from '../../components/ui/Button';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import etablissement from '../../assets/images/Etablissement2.webp';
import quiSommeNous from '../../assets/images/QuiSommeNous.webp';
import nosInstallations from '../../assets/images/NosInstallations.webp';

interface Value {
  title: string;
  description: string;
}

const SchoolPage = () => {
  const values: Value[] = [
    {
      title: 'Excellence',
      description: "Nous visons l'excellence dans tous les aspects de notre enseignement et encourageons nos élèves à donner le meilleur d'eux-mêmes.",
    },
    {
      title: 'Respect',
      description: "Le respect mutuel est au cœur de notre communauté scolaire, favorisant un environnement d'apprentissage harmonieux.",
    },
    {
      title: 'Innovation',
      description: "Nous adoptons des approches pédagogiques innovantes pour préparer nos élèves aux défis du 21ème siècle.",
    },
    {
      title: 'Responsabilité',
      description: "Nous enseignons à nos élèves l'importance de la responsabilité individuelle et collective.",
    },
    {
      title: 'Ouverture',
      description: "Nous valorisons la diversité et encourageons l'ouverture d'esprit et la tolérance envers les différentes cultures.",
    },
    {
      title: 'Intégrité',
      description: "Nous inculquons l'honnêteté et l'intégrité comme valeurs fondamentales dans toutes nos actions.",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageBanner
        title="Notre École"
        subtitle="Découvrez le Groupe Scolaire Sanaa"
        imageSrc={etablissement}
      />

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeading
                title="Qui sommes-nous ?"
                subtitle="Notre mission"
              />
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Le Groupe Scolaire Sanaa est un pionnier de l'enseignement à Casablanca, doté d'une expérience de plus de Vingt ans, desservant la maternelle, le primaire, le collège et le lycée avec un enseignement solidement ancré dans la pédagogie du 21ème siècle et construit autour d'un programme multilingue.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Fondé en 1997, notre établissement s'est constamment développé pour répondre aux besoins éducatifs croissants de la communauté, tout en maintenant notre engagement envers l'excellence académique et le développement intégral de chaque élève.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Notre mission est de former des citoyens responsables, créatifs et ouverts sur le monde, capables de s'adapter aux défis futurs tout en restant attachés à leurs valeurs culturelles.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-primary rounded-tl-3xl z-0"></div>
              <img 
                src={quiSommeNous}
                alt="Élèves en classe au Groupe Scolaire Sanaa" 
                className="rounded-lg shadow-elegant w-full h-full object-cover relative z-10"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-secondary rounded-br-3xl z-0"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Nos valeurs fondamentales"
            subtitle="Ce qui nous définit"
            centered={true}
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {values.map((value, index) => (
              <motion.div 
                key={value.title}
                className="bg-white p-6 rounded-lg shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <CheckCircleIcon className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Notre équipe pédagogique"
            subtitle="Des professionnels dévoués"
            centered={true}
          />
          <div className="max-w-3xl mx-auto mt-8">
            <p className="text-lg text-gray-700 text-center mb-8">
              Notre équipe pédagogique est composée d'enseignants qualifiés et expérimentés, dévoués à l'éducation et au bien-être de vos enfants. Chaque membre de notre équipe apporte son expertise et sa passion pour créer un environnement d'apprentissage stimulant et bienveillant.
            </p>
            <div className="flex justify-center">
              <Button to="/contact" variant="primary">
                Contactez notre équipe
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Facilities Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Nos installations"
            subtitle="Un environnement propice à l'apprentissage"
            centered={true}
          />
          <div className="grid md:grid-cols-2 gap-12 items-center mt-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Des salles de classe modernes et équipées des dernières technologies éducatives.</p>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Des laboratoires scientifiques pour les expériences pratiques et l'apprentissage expérimental.</p>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Une bibliothèque riche en ressources éducatives, livres et matériel multimédia.</p>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Des espaces sportifs pour les activités physiques et le développement moteur.</p>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Des aires de récréation sécurisées adaptées à chaque tranche d'âge.</p>
                </li>
                 <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-gray-700">Un service de restauration proposant des repas équilibrés et nutritifs.</p>
                </li>
              </ul>
            </motion.div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-primary rounded-tl-3xl z-0"></div>
              <img 
                src={nosInstallations}
                alt="Installations du Groupe Scolaire Sanaa" 
                className="rounded-lg shadow-elegant w-full h-full object-cover relative z-10"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-secondary rounded-br-3xl z-0"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Prêt à rejoindre notre communauté éducative ?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Contactez-nous dès aujourd'hui pour en savoir plus sur nos programmes et pour planifier une visite de notre établissement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button to="/contact" variant="outline-white" size="large">
              Nous contacter
            </Button>
            <Button to="/nos-cycles" variant="secondary" size="large">
              Découvrir nos programmes
            </Button>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default SchoolPage;
